"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useMemo } from "react";
import { objectFromEntries } from "./tools/polyfills/Object.fromEntries";
import { objectKeys } from "./tools/objectKeys";
import { createUseCssAndCx } from "./cssAndCx";
import { getDependencyArrayRef } from "./tools/getDependencyArrayRef";
import { typeGuard } from "./tools/typeGuard";
import { assert } from "./tools/assert";
import { mergeClasses } from "./mergeClasses";
import { createContext, useContext } from "react";
import { useMuiThemeStyleOverridesPlugin } from "./mui/themeStyleOverridesPlugin";
import { __unsafe_useEmotionCache as useContextualCache } from "@emotion/react";
var counter = 0;
export function createMakeStyles(params) {
  var useTheme = params.useTheme,
      cacheProvidedAtInception = params.cache;

  var _createUseCache = createUseCache({
    cacheProvidedAtInception: cacheProvidedAtInception
  }),
      useCache = _createUseCache.useCache;

  var _createUseCssAndCx = createUseCssAndCx({
    useCache: useCache
  }),
      useCssAndCx = _createUseCssAndCx.useCssAndCx;
  /** returns useStyle. */


  function makeStyles(params) {
    var _ref = params !== null && params !== void 0 ? params : {},
        nameOrWrappedName = _ref.name,
        _ref$uniqId = _ref.uniqId,
        uniqId = _ref$uniqId === void 0 ? "".concat(counter++) : _ref$uniqId;

    var name = typeof nameOrWrappedName !== "object" ? nameOrWrappedName : Object.keys(nameOrWrappedName)[0];
    return function (cssObjectByRuleNameOrGetCssObjectByRuleName) {
      var getCssObjectByRuleName = typeof cssObjectByRuleNameOrGetCssObjectByRuleName === "function" ? cssObjectByRuleNameOrGetCssObjectByRuleName : function () {
        return cssObjectByRuleNameOrGetCssObjectByRuleName;
      };
      return function useStyles(params, muiStyleOverridesParams) {
        var theme = useTheme();

        var _useCssAndCx = useCssAndCx(),
            css = _useCssAndCx.css,
            cx = _useCssAndCx.cx;

        var cache = useCache();
        var classes = useMemo(function () {
          var refClassesCache = {};
          var refClasses = typeof Proxy !== "undefined" && new Proxy({}, {
            "get": function get(_target, propertyKey) {
              if (typeof propertyKey === "symbol") {
                assert(false);
              }

              return refClassesCache[propertyKey] = "".concat(cache.key, "-").concat(uniqId).concat(name !== undefined ? "-".concat(name) : "", "-").concat(propertyKey, "-ref");
            }
          });
          var cssObjectByRuleName = getCssObjectByRuleName(theme, params, refClasses || {});
          var classes = objectFromEntries(objectKeys(cssObjectByRuleName).map(function (ruleName) {
            var cssObject = cssObjectByRuleName[ruleName];

            if (!cssObject.label) {
              cssObject.label = "".concat(name !== undefined ? "".concat(name, "-") : "").concat(ruleName);
            }

            return [ruleName, "".concat(css(cssObject)).concat(typeGuard(ruleName, ruleName in refClassesCache) ? " ".concat(refClassesCache[ruleName]) : "")];
          }));
          objectKeys(refClassesCache).forEach(function (ruleName) {
            if (ruleName in classes) {
              return;
            }

            classes[ruleName] = refClassesCache[ruleName];
          });
          return classes;
        }, [cache, css, cx, theme, getDependencyArrayRef(params)]);
        {
          var propsClasses = muiStyleOverridesParams === null || muiStyleOverridesParams === void 0 ? void 0 : muiStyleOverridesParams.props.classes;
          classes = useMemo(function () {
            return mergeClasses(classes, propsClasses, cx);
          }, [classes, getDependencyArrayRef(propsClasses), cx]);
        }
        {
          var pluginResultWrap = useMuiThemeStyleOverridesPlugin({
            classes: classes,
            css: css,
            cx: cx,
            "name": name !== null && name !== void 0 ? name : "makeStyle no name",
            "idOfUseStyles": uniqId,
            muiStyleOverridesParams: muiStyleOverridesParams,
            // NOTE: If it's not a Mui Theme the plugin is resilient, it will not crash
            "theme": theme
          });

          if (pluginResultWrap.classes !== undefined) {
            classes = pluginResultWrap.classes;
          }

          if (pluginResultWrap.css !== undefined) {
            css = pluginResultWrap.css;
          }

          if (pluginResultWrap.cx !== undefined) {
            cx = pluginResultWrap.cx;
          }
        }
        return {
          classes: classes,
          theme: theme,
          css: css,
          cx: cx
        };
      };
    };
  }

  function useStyles() {
    var theme = useTheme();

    var _useCssAndCx2 = useCssAndCx(),
        css = _useCssAndCx2.css,
        cx = _useCssAndCx2.cx;

    return {
      theme: theme,
      css: css,
      cx: cx
    };
  }

  return {
    makeStyles: makeStyles,
    useStyles: useStyles
  };
}
var reactContext = createContext(undefined);
export function TssCacheProvider(props) {
  var children = props.children,
      value = props.value;
  return React.createElement(reactContext.Provider, {
    value: value
  }, children);
}

var _ref2 = function () {
  function useCacheProvidedByProvider() {
    var cacheExplicitlyProvidedForTss = useContext(reactContext);
    return cacheExplicitlyProvidedForTss;
  }

  function createUseCache(params) {
    var cacheProvidedAtInception = params.cacheProvidedAtInception;

    function useCache() {
      var _a;

      var contextualCache = useContextualCache();
      var cacheExplicitlyProvidedForTss = useCacheProvidedByProvider();
      var cacheToBeUsed = (_a = cacheProvidedAtInception !== null && cacheProvidedAtInception !== void 0 ? cacheProvidedAtInception : cacheExplicitlyProvidedForTss) !== null && _a !== void 0 ? _a : contextualCache;

      if (cacheToBeUsed === null) {
        throw new Error(["In order to get SSR working with tss-react you need to explicitly provide an Emotion cache.", "MUI users be aware: This is not an error strictly related to tss-react, with or without tss-react,", "MUI needs an Emotion cache to be provided for SSR to work.", "Here is the MUI documentation related to SSR setup: https://mui.com/material-ui/guides/server-rendering/", "TSS provides helper that makes the process of setting up SSR easier: https://docs.tss-react.dev/ssr"].join("\n"));
      }

      return cacheToBeUsed;
    }

    return {
      useCache: useCache
    };
  }

  return {
    createUseCache: createUseCache
  };
}(),
    createUseCache = _ref2.createUseCache;

export { createUseCache };